import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { useInViewport } from '../hooks/use-in-viewport';
import { cn } from '../lib/utils';

/**
 * Defers rendering of children until the element is near the viewport,
 * then reveals them with a height + opacity CSS animation.
 *
 * Uses the CSS `grid-template-rows: 0fr → 1fr` technique to animate
 * from collapsed to intrinsic height without measuring.
 *
 * @example
 * const LazyNewsletter = lazy(() => import('./newsletter'));
 *
 * <RevealOnScroll rootMargin="200px">
 *   <LazyNewsletter />
 * </RevealOnScroll>
 */
export function RevealOnScroll({
	children,
	rootMargin = '200px',
	className,
	duration = 500,
}: {
	children: ReactNode;
	/** How far outside the viewport to start loading (default `"200px"`). */
	rootMargin?: string;
	className?: string;
	/** Transition duration in ms (default `500`). */
	duration?: number;
}) {
	const [ref, isVisible] = useInViewport({ rootMargin });
	const [expanded, setExpanded] = useState(false);

	return (
		<div
			ref={ref}
			className={cn(
				'grid transition-[grid-template-rows,opacity] ease-out',
				expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
				className,
			)}
			style={{ transitionDuration: `${duration}ms` }}
		>
			<div className="min-h-0 overflow-hidden">
				{isVisible && (
					<Suspense>
						<AnimateAfterMount onReady={() => setExpanded(true)}>{children}</AnimateAfterMount>
					</Suspense>
				)}
			</div>
		</div>
	);
}

/**
 * Waits for Suspense to resolve (i.e. lazy children have loaded),
 * then triggers the expand on the next frame so the browser can
 * paint the collapsed state first.
 */
function AnimateAfterMount({ children, onReady }: { children: ReactNode; onReady: () => void }) {
	useEffect(() => {
		const id = requestAnimationFrame(() => {
			requestAnimationFrame(() => onReady());
		});
		return () => cancelAnimationFrame(id);
	}, [onReady]);

	return children;
}
